/**
 * Everything between a signed-in user and their first order, in one modal.
 *
 * `domain/trading-setup.ts` decides the steps; this renders the first one still
 * to do and the list of them above it, and the ticket's `ENABLE TRADING` button
 * opens it whichever step is missing. Two steps, in order:
 *
 * 1. **One-click trading** — the session-key signature (`<SessionKeyStep>`).
 * 2. **Add funds** — USDC from the wallet into the trading balance. **One
 *    signature and no gas** when the venue relays (`backend/GASLESS-PLAN.md`):
 *    the user signs and the venue sends the transaction. With no relayer it is
 *    the direct path from the user's own wallet — `Allow USDC`, then `Add
 *    funds`, two buttons for `<AddFundsPanel>`'s reason: one click behind two
 *    popups is a second popup the user did not expect.
 *
 * **There is no approval step.** Adding funds lets the settlement contract
 * settle the user's trades from the trading balance; until gasless funding
 * that was a third step, `Approve USDU`, and a fourth, `setApprovalForAll` on
 * the outcome tokens, until `docs/06` §F93.
 *
 * It never says "USDU" (`GASLESS-PLAN.md` decision 5): the user sees dollars
 * and a trading balance. `docs/07` §6's disclosure — that the venue works out
 * what moves at settlement, can freeze an account and can upgrade the contract
 * — is said here, before the money is added, in those words.
 *
 * Only the steps that were missing are listed. It is presentational: every
 * action is a prop and `<WalletProvider>` owns the state, which is why the
 * wallet chunk stays behind `wallet/lazy.ts` (invariant 13).
 *
 * **Add funds carries the chain-trust notice**, as every panel that moves money
 * does (`<ChainTrustNotice>`), and its button is disabled on the same
 * condition.
 *
 * **A wallet on the wrong network gets a button that switches it, not an
 * error.** Both steps need the right network: the wallet refuses the
 * session-key signature anywhere else, and the balances cannot be read. So the
 * primary button becomes "Switch to {network} and enable trading", which
 * switches and then asks for the signature, or "Switch to {network}" on Add
 * funds, which then shows the amount it could not read before. The switch only
 * ever happens from that click (`<WalletProvider>`'s `ensureVenueChain`).
 *
 * **The first step keeps its old title and button** — `Enable one-click
 * trading` / `Enable trading` — because that is the modal's name to every user
 * and every browser test that arms a key.
 */

import { useEffect, useRef, useState } from 'react'
import { Button, Glyph, Input, Modal } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { formatFixed, parseFixed, USDC_DP } from '@/lib/decimal'
import { formatUsdc } from '@/lib/format'
import { SETUP_ORDER, type SetupStep } from '@/domain/trading-setup'
import { withdrawalSpeedSentence } from '@/domain/unwrap'
import type { IdentityState } from '@/wallet/identity'
import type { WrapperState } from '@/wallet/usdu'
import type { ChainTrustVerdict } from '@/domain/chain-trust'
import { chainName } from '@/domain/chain-trust'
import { ChainTrustNoticeView } from './ChainTrustNotice'
import { WrongChainNotice } from './WrongChainNotice'
import { SessionKeyStep } from './EnableTradingModal'

export interface TradingSetupModalProps {
  open: boolean
  onClose: () => void
  /** Every step this run has needed — what the list shows, done or not. */
  planned: readonly SetupStep[]
  /** The steps still to do. The first is the one on screen. */
  remaining: readonly SetupStep[]
  /**
   * Every step this run needed is done. The modal shows the ready tick and
   * closes itself shortly after.
   */
  complete: boolean
  identity: IdentityState
  /** `config.session_keys.key_ttl_ms`. */
  ttlMs: number
  wrapper: WrapperState
  busy: boolean
  error: string | null
  /** The wallet is on another network. Both steps then offer the switch instead. */
  chainMismatch: { wallet: number; venue: number } | null
  /**
   * Whether this browser will act on the venue's contracts. Adding funds
   * names them. The session-key step only signs for the venue, so the contract
   * checks do not gate it; the wallet's network does, through `chainMismatch`.
   */
  chainTrust: ChainTrustVerdict
  /** Accept a changed pair — the notice's own button. */
  onTrustAddresses: () => void
  /** The venue sends funding transactions: one signature, no gas. */
  relayEnabled: boolean
  /** Switches to the venue's network first when `chainMismatch` is set, then arms. */
  onArm: () => void
  /** Switch to the venue's network, and nothing else. */
  onSwitchChain: () => void
  /** Direct path only: let the trading balance take this much USDC first. */
  onAllowFunds: (amountMicroUsdc: bigint) => void
  /** Add funds — gasless when `relayEnabled`, else the direct deposit. */
  onAddFunds: (amountMicroUsdc: bigint) => void
}

const TITLE: Record<SetupStep, string> = {
  SESSION_KEY: 'Enable one-click trading',
  ADD_FUNDS: 'Add funds',
}

const STEP_NAME: Record<SetupStep, string> = {
  SESSION_KEY: 'One-click trading',
  ADD_FUNDS: 'Add funds',
}

/** How long the ready tick stays before the modal closes itself. */
const READY_HOLD_MS = 1800

export function TradingSetupModal(props: TradingSetupModalProps) {
  const { open, onClose, planned, remaining, complete, busy, error, chainMismatch, chainTrust } = props
  const current = complete ? null : (remaining[0] ?? null)
  const untrusted = !chainTrust.trusted && current === 'ADD_FUNDS'
  const funds = useFundsAmount(props.wrapper.backing, current === 'ADD_FUNDS')

  // Closed on a timer once complete. `onClose` is read through a ref for
  // `<Modal>`'s reason: callers pass it inline, and as a dependency it would
  // restart the timer on every render.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })
  useEffect(() => {
    if (!open || !complete) return
    const id = globalThis.setTimeout(() => onCloseRef.current(), READY_HOLD_MS)
    return () => globalThis.clearTimeout(id)
  }, [open, complete])

  if (complete) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title="You're ready to trade"
        footer={
          <Button variant="primary" onClick={onClose}>
            Start trading
          </Button>
        }
      >
        <div className="flex flex-col items-center gap-3 py-2 text-center" data-testid="setup-ready">
          <ReadyTick />
          <p className="text-body text-text-primary">You're all set to trade.</p>
        </div>
      </Modal>
    )
  }

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="ghost" onClick={onClose}>
        Not now
      </Button>
      {current ? <PrimaryAction {...props} current={current} funds={funds} untrusted={untrusted} /> : null}
    </div>
  )

  return (
    <Modal open={open} onClose={onClose} title={current ? TITLE[current] : 'Setting up trading'} footer={footer}>
      {planned.length > 1 ? <StepList planned={planned} remaining={remaining} /> : null}

      {current === 'SESSION_KEY' ? <SessionKeyStep identity={props.identity} ttlMs={props.ttlMs} /> : null}
      {current === 'ADD_FUNDS' ?
        <AddFundsStep wrapper={props.wrapper} funds={funds} relayEnabled={props.relayEnabled} />
      : null}
      {current === null ? (
        <p className="text-body text-text-secondary">
          {busy ? 'Waiting for your wallet…'
          : planned.length === 0 ? 'Nothing left to set up. You can trade.'
          : 'Checking your wallet…'}
        </p>
      ) : null}

      {/* No button of its own: the primary button is the switch. */}
      {chainMismatch ? <WrongChainNotice mismatch={chainMismatch} className="mt-3" /> : null}

      {untrusted ?
        <ChainTrustNoticeView verdict={chainTrust} onTrust={props.onTrustAddresses} className="mt-3" />
      : null}

      {/* The refusal a send raised is the notice's own sentence; said once. */}
      {error && !((untrusted || chainMismatch) && !chainTrust.trusted && error === chainTrust.detail) ? (
        <p className="mt-3 rounded-sm bg-danger-bg px-2 py-1_5 text-caption text-danger-text">{error}</p>
      ) : null}
    </Modal>
  )
}

// ---------------------------------------------------------------------------

/** A green ring that pops in and a check that draws itself (`base.css`). */
function ReadyTick() {
  return (
    <svg
      viewBox="0 0 52 52"
      aria-hidden="true"
      className="h-[calc(64*var(--ud-u))] w-[calc(64*var(--ud-u))] text-success-text"
    >
      <circle
        className="ud-tick-ring"
        cx="26"
        cy="26"
        r="24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="ud-tick-check"
        d="M15 27 l7 7 l15 -15"
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function StepList({ planned, remaining }: { planned: readonly SetupStep[]; remaining: readonly SetupStep[] }) {
  const steps = SETUP_ORDER.filter((step) => planned.includes(step))
  const current = remaining[0]
  return (
    <ol className="mb-3 flex flex-wrap gap-x-3 gap-y-1 text-caption" data-testid="setup-steps">
      {steps.map((step, i) => {
        const done = !remaining.includes(step)
        return (
          <li
            key={step}
            aria-current={step === current ? 'step' : undefined}
            className={cx(
              step === current ? 'text-text-primary'
              : done ? 'text-success-text'
              : 'text-text-tertiary',
            )}
          >
            {done ?
              <Glyph name="check" label="done" />
            : `${i + 1}.`}{' '}
            {STEP_NAME[step]}
          </li>
        )
      })}
    </ol>
  )
}

interface FundsAmount {
  text: string
  setText: (text: string) => void
  micro: bigint | null
}

/**
 * The amount field, pre-filled with the whole USDC balance until the user edits
 * it, and back to that once the step is no longer on screen.
 */
function useFundsAmount(backing: bigint | null, active: boolean): FundsAmount {
  const [edited, setEdited] = useState<string | null>(null)
  if (!active && edited !== null) setEdited(null)
  const text = edited ?? (backing !== null && backing > 0n ? twoDecimals(backing) : '')
  return { text, setText: setEdited, micro: parseAmount(text) }
}

function AddFundsStep({
  wrapper,
  funds,
  relayEnabled,
}: {
  wrapper: WrapperState
  funds: FundsAmount
  relayEnabled: boolean
}) {
  const over = funds.micro !== null && wrapper.backing !== null && funds.micro > wrapper.backing
  return (
    <>
      <p className="text-body text-text-primary">
        Move USDC from your wallet into your trading balance — one for one, with no
        fee. {relayEnabled ?
          'No ETH needed: you sign, and we pay the network fee.'
        : 'This is a transaction from your wallet, so it needs a little ETH for the network fee.'}{' '}
        {withdrawalSpeedSentence(relayEnabled)}
      </p>
      <div className="mt-3">
        <Input
          label="Add"
          unit="USDC"
          value={funds.text}
          onChange={(event) => funds.setText(event.target.value)}
          {...(over ? { error: 'Not enough USDC in your wallet.' } : {})}
          className="w-[calc(192*var(--ud-u))]"
        />
        {wrapper.backing !== null ? (
          <p className="mt-1 text-caption text-text-tertiary">
            {formatUsdc(formatFixed(wrapper.backing, USDC_DP))} USDC in your wallet.
          </p>
        ) : null}
      </div>
      <p className="mt-3 text-caption text-text-tertiary">
        Adding funds lets UpTik settle your trades from this balance: when a market
        you traded ends, we work out what you won or owe, and the contract moves it.
        We can freeze an account we believe is manipulating the orderbook. The
        contract can also be upgraded by our multi-sig, and every upgrade is visible
        on-chain.
      </p>
    </>
  )
}

function PrimaryAction(props: TradingSetupModalProps & { current: SetupStep; funds: FundsAmount; untrusted: boolean }) {
  const { current, busy, wrapper, funds, untrusted, relayEnabled, chainMismatch } = props
  switch (current) {
    case 'SESSION_KEY':
      return (
        <Button variant="primary" loading={busy} onClick={props.onArm}>
          {chainMismatch ? `Switch to ${chainName(chainMismatch.venue)} and enable trading` : 'Enable trading'}
        </Button>
      )
    case 'ADD_FUNDS': {
      // No amount to add yet: the balance is unreadable on the wrong network.
      if (chainMismatch) {
        return (
          <Button variant="primary" loading={busy} onClick={props.onSwitchChain}>
            Switch to {chainName(chainMismatch.venue)}
          </Button>
        )
      }
      const amount = funds.micro
      const valid = amount !== null && wrapper.backing !== null && amount <= wrapper.backing
      // The direct path pulls USDC with `transferFrom`, so it needs an
      // allowance first — and an unread allowance is not treated as zero. The
      // gasless path needs none: the signature is the authorisation.
      const needsAllow =
        !relayEnabled && valid && wrapper.wrapperAllowance !== null && wrapper.wrapperAllowance < amount
      const unread = !relayEnabled && wrapper.wrapperAllowance === null
      return needsAllow ?
          <Button variant="primary" loading={busy} disabled={untrusted} onClick={() => props.onAllowFunds(amount)}>
            Allow USDC
          </Button>
        : <Button
            variant="primary"
            loading={busy}
            disabled={untrusted || !valid || unread}
            onClick={() => amount !== null && props.onAddFunds(amount)}
          >
            Add funds
          </Button>
    }
  }
}

/**
 * A balance as a two-decimal amount, **floored** to the cent: rounding up would
 * pre-fill more than the wallet holds, and the field would refuse its own value.
 */
function twoDecimals(micro: bigint): string {
  return formatFixed(micro / 10_000n, 2)
}

function parseAmount(value: string): bigint | null {
  try {
    const micro = parseFixed(value.trim(), USDC_DP)
    return micro > 0n ? micro : null
  } catch {
    return null
  }
}
